import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import './OrderReview.css';
import { useAuth } from '../../contexts/AuthContext';
import { clearCart, getCartItems } from '../../firebase/Cart';
import { createOrder } from '../../firebase/orders';
import { getErrorMessage } from '../../utils/errorUtils';
import ShirtPreviewModal from '../ShirtPreviewModal/ShirtPreviewModal';
import { ShirtPhotoCanvas } from '../ShirtPreviewModal/ShirtPreviewModal';

// ─── Subcategory constants ─────────────────────────────────────────────────────
const SUBCATEGORY_TSHIRT = 'T-Shirt';
const SUBCATEGORY_JERSEY = 'Jersey';
const SUBCATEGORY_POLO   = 'Polo Shirt';

function isJerseyItem(item) { return item?.subcategory === SUBCATEGORY_JERSEY; }
function isPoloItem(item)   { return item?.subcategory === SUBCATEGORY_POLO;   }
function isTShirtItem(item) {
    const sub = item?.subcategory;
    return !sub || sub === SUBCATEGORY_TSHIRT;
}
function isPoloOrJersey(item) { return isJerseyItem(item) || isPoloItem(item); }
function isGenericProduct(item) {
    const sub = item?.subcategory;
    if (!sub) return false;
    return sub !== SUBCATEGORY_TSHIRT && sub !== SUBCATEGORY_JERSEY && sub !== SUBCATEGORY_POLO;
}

// ─── Banner copy per subcategory ──────────────────────────────────────────────
function getBannerInfo(item) {
    if (isJerseyItem(item)) return { title: 'Jersey',     subtitle: 'Customize your jersey with team details and sizing.' };
    if (isPoloItem(item))   return { title: 'Polo Shirt', subtitle: 'Select your size and quantity to order.' };
    if (isGenericProduct(item)) return { title: item?.productName || item?.subcategory || 'Product', subtitle: 'Review your product details and finalize your order.' };
    return                         { title: 'T-Shirts',   subtitle: 'Design your own T-shirt — soft, stylish, and made for you.' };
}

// ─── Pricing tables ───────────────────────────────────────────────────────────
const SHIRT_SIZE_PRICES = {
    'Blue Corner White T-Shirt':        { xs:150, small:150, medium:150, large:160, xl:165, xxl:170, xxxl:175, xxxxl:180, xxxxxl:200 },
    'Blue Corner Colored T-Shirt':      { xs:180, small:180, medium:180, large:195, xl:195, xxl:205, xxxl:210, xxxxl:220, xxxxxl:235 },
    'Yalex White T-Shirt':              { xs:120, small:120, medium:120, large:120, xl:125, xxl:130, xxxl:135, xxxxl:null, xxxxxl:140 },
    'Yalex Colored T-Shirt':            { xs:125, small:125, medium:125, large:125, xl:130, xxl:135, xxxl:140, xxxxl:null, xxxxxl:150 },
    'Pro Club Inspire White T-Shirt':   { xs:null, small:200, medium:200, large:200, xl:220, xxl:230, xxxl:null, xxxxl:null, xxxxxl:null },
    'Pro Club Inspire Colored T-Shirt': { xs:null, small:250, medium:250, large:250, xl:260, xxl:270, xxxl:null, xxxxl:null, xxxxxl:null },
    'Blackhorse White T-Shirt':         { xs:120, small:120, medium:120, large:120, xl:125, xxl:130, xxxl:null, xxxxl:null, xxxxxl:null },
    'Blackhorse Colored T-Shirt':       { xs:125, small:125, medium:125, large:125, xl:130, xxl:135, xxxl:null, xxxxl:null, xxxxxl:null },
    'Active Life White T-Shirt':        { xs:120, small:120, medium:120, large:120, xl:125, xxl:130, xxxl:135, xxxxl:null, xxxxxl:140 },
    'Active Life Colored T-Shirt':      { xs:125, small:125, medium:125, large:125, xl:130, xxl:135, xxxl:140, xxxxl:null, xxxxxl:150 },
    'Drift White T-Shirt':              { xs:85, small:85, medium:85, large:85, xl:90, xxl:95, xxxl:null, xxxxl:null, xxxxxl:null },
    'Drift Colored T-Shirt':            { xs:95, small:95, medium:95, large:95, xl:105, xxl:115, xxxl:null, xxxxl:null, xxxxxl:null },
};
const IMPRINT_PRICES_RECTANGLE = {
    'Full Front':150,'Medium Front':120,'Center Chest':50,'Across Chest':80,
    'Left Chest':50,'Right Chest':50,'Left Vertical':120,'Right Vertical':120,
    'Bottom Left':50,'Bottom Right':50,'Full Back':150,'Medium Back':120,
    'Locker Center Patch':50,'Across Shoulder':80,'Left Sleeve':40,'Right Sleeve':40,
};
const IMPRINT_PRICES_CIRCLE = {
    'Full Front':120,'Medium Front':80,'Center Chest':50,'Across Chest':80,
    'Left Chest':40,'Right Chest':40,'Left Vertical':100,'Right Vertical':100,
    'Bottom Left':50,'Bottom Right':50,'Full Back':120,'Medium Back':80,
    'Locker Center Patch':50,'Back Center':80,'Left Sleeve':40,'Right Sleeve':40,
};

const ALL_SIZES = ['xxs','xs','small','medium','large','xl','xxl','xxxl','xxxxl','xxxxxl'];

function mergeSelectedCartItems(baseItems = [], latestCartItems = []) {
    if (!Array.isArray(baseItems) || baseItems.length === 0) return [];
    const latestById = new Map(latestCartItems.map((item) => [item.id, item]));
    return baseItems.map((item) => {
        const latest = latestById.get(item.id);
        if (!latest) return item;
        return {
            ...item,
            quantities: latest.quantities || item.quantities || {},
            quantity: latest.quantity || item.quantity || 0,
            color: latest.color || item.color || '',
            designShape: latest.designShape || item.designShape || '',
            printLocation: latest.printLocation || item.printLocation || '',
            designImages: latest.designImages || item.designImages || {},
            sizeBreakdown: latest.sizeBreakdown || item.sizeBreakdown || '',
            teamName: latest.teamName || item.teamName || '',
            surname: latest.surname || item.surname || '',
            number: latest.number || item.number || '',
            designEmailSent: Boolean(latest.designEmailSent || item.designEmailSent),
            designEmailSentAt: latest.designEmailSentAt || item.designEmailSentAt || null,
            storeEmail: latest.storeEmail || item.storeEmail || '',
        };
    });
}

// ─── Item total calculators ───────────────────────────────────────────────────
// FIX: calcTShirtTotal now includes colorSurcharge per piece in shirt subtotal
function calcTShirtTotal(item) {
    const quantities     = item.quantities || {};
    const totalQty       = Object.values(quantities).reduce((s, q) => s + (q || 0), 0);
    if (totalQty === 0) return { shirtSubtotal: 0, imprintSubtotal: 0, itemTotal: 0, totalQty: 0 };

    const sizePrices     = (item.sizePrices && Object.keys(item.sizePrices).length > 0)
        ? item.sizePrices
        : (SHIRT_SIZE_PRICES[item.type] || {});
    const colorSurcharge = Number(item.colorSurcharge || 0);

    // Include colorSurcharge per piece in shirt subtotal
    const shirtSubtotal = Object.entries(quantities).reduce(
        (s, [size, q]) => s + (q || 0) * ((Number(sizePrices[size]) || Number(item.price) || 0) + colorSurcharge),
        0
    );

    const shape           = (item.designShape || '').toLowerCase() === 'circle' ? 'circle' : 'rectangle';
    const table           = shape === 'circle' ? IMPRINT_PRICES_CIRCLE : IMPRINT_PRICES_RECTANGLE;
    const locs            = (item.printLocation || '').split(',').map(s => s.trim()).filter(Boolean);
    const prices          = locs.map(l => table[l] || 0).sort((a, b) => b - a);
    const imprintPerPc    = prices.reduce((s, p, i) => s + (i === 0 ? p : Math.round(p * 0.5)), 0);
    const imprintSubtotal = imprintPerPc * totalQty;

    return { totalQty, shirtSubtotal, imprintSubtotal, itemTotal: shirtSubtotal + imprintSubtotal };
}

function calcPoloJerseyTotal(item) {
    const quantities       = item.quantities || {};
    const totalQty         = Object.values(quantities).reduce((s, q) => s + (q || 0), 0);
    const storedSizePrices = item.sizePrices || {};
    const hasSizePrices    = Object.keys(storedSizePrices).length > 0;

    const shirtSubtotal = ALL_SIZES.reduce((sum, sz) => {
        const qty   = quantities[sz] || 0;
        const price = hasSizePrices
            ? (storedSizePrices[sz] ?? item.price ?? 0)
            : (item.price ?? 0);
        return sum + qty * price;
    }, 0);

    return { totalQty, shirtSubtotal, imprintSubtotal: 0, itemTotal: shirtSubtotal };
}

function calcGenericTotal(item) {
    const qty = Number(item.quantity) || 0;
    const price = Number(item.price) || 0;
    const total = qty * price;
    return { totalQty: qty, shirtSubtotal: total, imprintSubtotal: 0, itemTotal: total };
}

function calcItemTotal(item) {
    if (isPoloOrJersey(item)) return calcPoloJerseyTotal(item).itemTotal;
    if (isGenericProduct(item)) return calcGenericTotal(item).itemTotal;
    return calcTShirtTotal(item).itemTotal;
}

function getItemBreakdown(item) {
    if (isPoloOrJersey(item)) return calcPoloJerseyTotal(item);
    if (isGenericProduct(item)) return calcGenericTotal(item);
    return calcTShirtTotal(item);
}

// ─── Color helper ─────────────────────────────────────────────────────────────
const COLOR_HEX_MAP = {
    'black':'#1f1f1f', 'red':'#EF4444', 'yellow':'#FCD34D', 'orange':'#F97316',
    'white':'#f5f5f5', 'blue':'#1a5faa', 'green':'#2e7d32', 'navy':'#1a237e',
    'gray':'#888888', 'pink':'#e91e8c', 'purple':'#7b1fa2', 'brown':'#5d4037',
};

function ColorSwatch({ colorKey }) {
    if (!colorKey) return null;
    const hex     = COLOR_HEX_MAP[colorKey.toLowerCase()] || '#cccccc';
    const isLight = ['white', 'yellow'].includes(colorKey.toLowerCase());
    return (
        <span style={{
            display:'inline-block', width:13, height:13, borderRadius:'50%',
            backgroundColor:hex, border: isLight ? '1.5px solid #ccc' : 'none',
            marginRight:5, verticalAlign:'middle', position:'relative', top:-1,
        }} />
    );
}

// ─── T-Shirt preview helpers ──────────────────────────────────────────────────
const LOCATION_TO_SIDE = {
    'Full Front':'front','Medium Front':'front','Center Chest':'front',
    'Across Chest':'front','Left Chest':'front','Right Chest':'front',
    'Left Vertical':'front','Right Vertical':'front',
    'Bottom Left':'front','Bottom Right':'front',
    'Full Back':'back','Medium Back':'back','Back Center':'back',
    'Locker Center Patch':'back','Across Shoulder':'back',
    'Left Sleeve':'left','Right Sleeve':'right',
};
const SIDE_LABELS = { front:'Front', back:'Back', left:'Left Sleeve', right:'Right Sleeve' };

function getItemSides(item) {
    const locs  = (item.printLocation || '').split(',').map(s => s.trim()).filter(Boolean);
    const sides = new Set(locs.map(l => LOCATION_TO_SIDE[l]).filter(Boolean));
    return ['front','back','left','right'].filter(s => sides.has(s));
}

function SidePreview({ item, side }) {
    const locs         = (item.printLocation || '').split(',').map(s => s.trim()).filter(Boolean)
                            .filter(l => LOCATION_TO_SIDE[l] === side);
    const designImages = item.designImages || {};
    const isBack       = side === 'back';
    const isCircle     = (item.designShape || '').toLowerCase() === 'circle';
    const sideDesigns = useMemo(() => {
        const entries = locs
            .map((loc) => [loc, designImages[loc]])
            .filter(([, url]) => Boolean(url))
            .map(([loc, url]) => {
                if (isBack && loc === 'Left Sleeve') return ['Left Sleeve Back', url];
                if (isBack && loc === 'Right Sleeve') return ['Right Sleeve Back', url];
                return [loc, url];
            });
        return Object.fromEntries(entries);
    }, [locs, designImages, isBack]);

    const hasDesigns = Object.keys(sideDesigns).length > 0;

    return (
        <div className="side-preview-wrapper">
            <ShirtPhotoCanvas
                designs={sideDesigns}
                isBack={isBack}
                isCircle={isCircle}
                shirtColor={item.color || ''}
                canvasStyle={{ width: '100%', height: '100%', display: 'block' }}
            />
            {!hasDesigns && (
                <div className="side-preview-empty">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#bbb" strokeWidth="1.5">
                        <rect x="3" y="3" width="18" height="18" rx="2"/>
                        <path d="M3 9l4-4 4 4 4-6 4 6"/>
                        <circle cx="8.5" cy="8.5" r="1.5"/>
                    </svg>
                    <span>No design uploaded</span>
                </div>
            )}
        </div>
    );
}

function ProductImagePreview({ item }) {
    const imgSrc   = item.imageUrl || item.image || null;
    const isJersey = isJerseyItem(item);
    const label    = isJersey ? 'Basketball Jersey' : 'Polo Shirt';

    return (
        <div className="polo-jersey-preview-wrapper">
            {imgSrc ? (
                <img src={imgSrc} alt={item.name || label} className="polo-jersey-preview-img" />
            ) : (
                <div className="polo-jersey-preview-placeholder">
                    <span className="polo-jersey-preview-icon">👕</span>
                    <span className="polo-jersey-preview-label">{item.name || label}</span>
                </div>
            )}
            <div className="polo-jersey-design-badge">
                <span className="polo-jersey-design-badge-icon">🎨</span>
                <span className="polo-jersey-design-badge-text">Design by JG Printing</span>
            </div>
        </div>
    );
}

// ─── Order Details panel — subcategory-aware ──────────────────────────────────
function OrderDetailsPanel({ item, index }) {
    const { totalQty, shirtSubtotal, imprintSubtotal, itemTotal } = getItemBreakdown(item);
    const isJersey       = isJerseyItem(item);
    const isPolo         = isPoloItem(item);
    const isTShirt       = isTShirtItem(item);
    const isGeneric      = isGenericProduct(item);
    const colorSurcharge = isTShirt && !isGeneric ? Number(item.colorSurcharge || 0) : 0;
    const productLabel   = isGeneric ? (item.subcategory || item.category || 'Product')
        : isJersey ? 'Jersey' : isPolo ? 'Polo Shirt' : 'T-Shirt';

    return (
        <div className="order-details-box">
            <h3 className="details-heading">Order Details — Item #{index + 1}</h3>
            <div className="order-details-content">

                <div className="detail-row">
                    <span className="detail-label">Product:</span>
                    <span className="detail-value">{productLabel}</span>
                </div>
                <div className="detail-row">
                    <span className="detail-label">Type:</span>
                    <span className="detail-value">{item.type || item.name}</span>
                </div>

                {/* T-Shirt only: Color (with surcharge), Design Shape, Print Location */}
                {isTShirt && !isGeneric && (
                    <>
                        <div className="detail-row">
                            <span className="detail-label">Color:</span>
                            <span className="detail-value">
                                {item.color ? (
                                    <>
                                        <ColorSwatch colorKey={item.color} />
                                        {item.color.charAt(0).toUpperCase() + item.color.slice(1)}
                                        {colorSurcharge > 0 && (
                                            <span style={{ marginLeft:6, fontSize:11, color:'#888' }}>
                                                (+₱{colorSurcharge}/pc)
                                            </span>
                                        )}
                                    </>
                                ) : '—'}
                            </span>
                        </div>
                        <div className="detail-row">
                            <span className="detail-label">Design Shape:</span>
                            <span className="detail-value">{item.designShape || '—'}</span>
                        </div>
                        <div className="detail-row">
                            <span className="detail-label">Print Location:</span>
                            <span className="detail-value">{item.printLocation || '—'}</span>
                        </div>
                    </>
                )}

                {/* Jersey only: Team Name, Surname, Number */}
                {isJersey && (
                    <>
                        {item.teamName && <div className="detail-row"><span className="detail-label">Team Name:</span><span className="detail-value">{item.teamName}</span></div>}
                        {item.surname  && <div className="detail-row"><span className="detail-label">Surname:</span><span className="detail-value">{item.surname}</span></div>}
                        {item.number   && <div className="detail-row"><span className="detail-label">Number:</span><span className="detail-value">{item.number}</span></div>}
                    </>
                )}

                {/* Polo: Design note */}
                {isPolo && (
                    <div className="detail-row">
                        <span className="detail-label">Design:</span>
                        <span className="detail-value" style={{ color:'#7b5ea7', fontStyle:'italic' }}>
                            Provided by JG Printing
                        </span>
                    </div>
                )}

                {/* Generic / Non-clothing: category + selected attributes */}
                {isGeneric && (
                    <>
                        {item.category && (
                            <div className="detail-row">
                                <span className="detail-label">Category:</span>
                                <span className="detail-value">{item.category}</span>
                            </div>
                        )}
                        {item.subcategory && item.subcategory !== item.category && (
                            <div className="detail-row">
                                <span className="detail-label">Subcategory:</span>
                                <span className="detail-value">{item.subcategory}</span>
                            </div>
                        )}
                        {item.selectedAttributes && Object.keys(item.selectedAttributes).length > 0 && (
                            Object.entries(item.selectedAttributes).map(([key, val]) => (
                                <div className="detail-row" key={key}>
                                    <span className="detail-label">{key}:</span>
                                    <span className="detail-value">
                                        {typeof val === 'object' && val !== null
                                            ? (val.value || val.label || JSON.stringify(val))
                                            : String(val)}
                                    </span>
                                </div>
                            ))
                        )}
                    </>
                )}

                <div className="detail-row">
                    <span className="detail-label">Sizes:</span>
                    <span className="detail-value">{isGeneric ? (item.sizeBreakdown || 'N/A') : (item.sizeBreakdown || '—')}</span>
                </div>
                <div className="detail-row">
                    <span className="detail-label">Total Quantity:</span>
                    <span className="detail-value">{(isGeneric && totalQty === 0) ? (Number(item.quantity) || 0) : totalQty} pcs</span>
                </div>

                <div className="detail-row">
                    <span className="detail-label">
                        {isGeneric ? 'Subtotal:' : isJersey ? 'Jersey Subtotal:' : isPolo ? 'Polo Subtotal:' : 'Shirt Subtotal:'}
                    </span>
                    <span className="detail-value">₱ {shirtSubtotal.toLocaleString()}</span>
                </div>

                {/* Color surcharge note — T-Shirt only */}
                {isTShirt && !isGeneric && colorSurcharge > 0 && totalQty > 0 && (
                    <div className="detail-row" style={{ fontSize:12, color:'#888' }}>
                        <span className="detail-label">Color surcharge:</span>
                        <span className="detail-value">included above (₱{colorSurcharge}/pc)</span>
                    </div>
                )}

                {/* Print subtotal — T-Shirt only */}
                {isTShirt && !isGeneric && imprintSubtotal > 0 && (
                    <div className="detail-row">
                        <span className="detail-label">Print Subtotal:</span>
                        <span className="detail-value">₱ {imprintSubtotal.toLocaleString()}</span>
                    </div>
                )}

                <div className="detail-row detail-row-total">
                    <span className="detail-label">Item Total:</span>
                    <span className="detail-value">₱ {itemTotal.toLocaleString()}</span>
                </div>

                {item.note && (
                    <div className="detail-row">
                        <span className="detail-label">Note:</span>
                        <span className="detail-value">{item.note}</span>
                    </div>
                )}
            </div>
        </div>
    );
}

// ─── Main component ───────────────────────────────────────────────────────────
function OrderReview() {
    const navigate       = useNavigate();
    const routerLocation = useLocation();
    const { currentUser, getUserProfile } = useAuth();

    const passedItems = useMemo(
        () => routerLocation.state?.selectedCartItems || [],
        [routerLocation.state]
    );
    const [cartItems,      setCartItems]      = useState(passedItems);
    const [submitting,     setSubmitting]     = useState(false);
    const [selectedIdx,    setSelectedIdx]    = useState(0);
    const [orderNote]      = useState('');
    const [confirmChecked, setConfirmChecked] = useState(false);
    const [savedAddresses, setSavedAddresses] = useState([]);
    const [addressLoading, setAddressLoading] = useState(true);
    const [userInfo,       setUserInfo]       = useState({ firstName:'', lastName:'' });
    const [activeSideTabs, setActiveSideTabs] = useState({});
    const [previewModalOpen, setPreviewModalOpen] = useState(false);
    const [submitError,    setSubmitError]    = useState(null);

    useEffect(() => {
        const load = async () => {
            if (!currentUser) { setAddressLoading(false); return; }
            const data = await getUserProfile(currentUser.uid);
            setSavedAddresses(data?.savedAddresses || []);
            if (data?.firstName || data?.lastName) {
                setUserInfo({ firstName: data.firstName || '', lastName: data.lastName || '' });
            } else if (currentUser.displayName) {
                const p = currentUser.displayName.split(' ');
                setUserInfo({ firstName: p[0] || '', lastName: p.slice(1).join(' ') || '' });
            } else {
                setUserInfo({ firstName: currentUser.email?.split('@')[0] || 'Customer', lastName: '' });
            }
            setAddressLoading(false);
        };
        load();
    }, [currentUser, getUserProfile]);

    useEffect(() => {
        let mounted = true;
        const syncCartItems = async () => {
            if (!currentUser?.uid || passedItems.length === 0) return;
            const { items: latestCartItems, error } = await getCartItems(currentUser.uid);
            if (!mounted || error) return;
            setCartItems(mergeSelectedCartItems(passedItems, latestCartItems));
        };
        syncCartItems();
        return () => { mounted = false; };
    }, [currentUser, passedItems]);

    useEffect(() => {
        const item  = cartItems[selectedIdx];
        if (!item) return;
        const sides = isTShirtItem(item) ? getItemSides(item) : [];
        setActiveSideTabs(prev => ({
            ...prev,
            [selectedIdx]: prev[selectedIdx] || sides[0] || 'front',
        }));
    }, [selectedIdx, cartItems]);

    const hasAddress   = savedAddresses.length > 0;
    const currentItem  = cartItems[selectedIdx] || null;
    const currentSides = (currentItem && isTShirtItem(currentItem)) ? getItemSides(currentItem) : [];
    const activeSide   = activeSideTabs[selectedIdx] || currentSides[0] || 'front';
    const isCircle     = (currentItem?.designShape || '').toLowerCase() === 'circle';

    const { title: bannerTitle, subtitle: bannerSubtitle } = currentItem
        ? getBannerInfo(currentItem)
        : { title:'T-Shirts', subtitle:'Design your own T-shirt — soft, stylish, and made for you.' };

    const handleSubmit = async () => {
        setSubmitError(null);
        if (!confirmChecked) { setSubmitError('Please confirm that you have reviewed your design and order details.'); return; }
        const hasZeroQty = cartItems.some(item => {
            if (isGenericProduct(item)) return (Number(item.quantity) || 0) === 0;
            const qty = Object.values(item.quantities || {}).reduce((s, q) => s + (q || 0), 0);
            return qty === 0;
        });
        if (hasZeroQty) {
            setSubmitError('One or more items have zero quantity. Please update quantities in your cart.');
            return;
        }
        if (!hasAddress || !currentUser) return;
        setSubmitting(true);
        try {
            const name    = `${userInfo.firstName} ${userInfo.lastName}`.trim() || 'Customer';
            const address = savedAddresses.find(a => a.isDefault) || savedAddresses[0];
            let itemsForOrder = cartItems;
            const { items: latestCartItems, error: cartItemsError } = await getCartItems(currentUser.uid);
            if (!cartItemsError && latestCartItems.length > 0) {
                itemsForOrder = mergeSelectedCartItems(cartItems, latestCartItems);
                setCartItems(itemsForOrder);
            }
            const { orderId, error } = await createOrder(
                currentUser.uid,
                name,
                itemsForOrder,
                orderNote,
                address,
                currentUser.email || ''
            );
            if (error) { setSubmitError(getErrorMessage(error, 'Failed to submit order.')); setSubmitting(false); return; }
            await clearCart(currentUser.uid);
            navigate('/order-approval-submitted', { state: { orderId, customerName: name } });
        } catch (err) {
            console.error(err);
            setSubmitError(getErrorMessage(err, 'An unexpected error occurred. Please try again.'));
        } finally {
            setSubmitting(false);
        }
    };

    const grandTotal = cartItems.reduce((s, item) => s + calcItemTotal(item), 0);

    if (addressLoading) return (
        <div style={{ width:'100%', backgroundColor:'#ffffff' }}>
            <div className="order-review-page">
                <div className="loading-state"><div className="spinner"/><p>Loading your order...</p></div>
            </div>
        </div>
    );

    if (cartItems.length === 0) return (
        <div style={{ width:'100%', backgroundColor:'#ffffff' }}>
            <div className="order-review-page">
                <div className="empty-state">
                    <p>No items selected for review.</p>
                    <button onClick={() => navigate('/cart')}>← Back to Cart</button>
                </div>
            </div>
        </div>
    );

    const canSubmit = confirmChecked && hasAddress && !submitting;

    return (
        <div style={{ width:'100%', backgroundColor:'#ffffff' }}>
            <div className="order-review-page">

                {currentItem && isTShirtItem(currentItem) && (
                    <ShirtPreviewModal
                        open={previewModalOpen}
                        onClose={() => setPreviewModalOpen(false)}
                        designImages={currentItem?.designImages || {}}
                        isCircle={isCircle}
                        shirtColor={currentItem?.color || ''}
                        title={`Preview — Order #${selectedIdx + 1}`}
                    />
                )}

                <div className="product-header-banner">
                    <h2 className="product-banner-title">{bannerTitle}</h2>
                    <p className="product-banner-subtitle">{bannerSubtitle}</p>
                </div>

                <div className="order-review-container">

                    {/* Left: Preview */}
                    <div className="preview-section">
                        <div className="preview-tabs">
                            <button className="preview-tab active">
                                {currentItem && isTShirtItem(currentItem) && currentSides.length > 0
                                    ? `Order #${selectedIdx + 1} — ${SIDE_LABELS[activeSide]}`
                                    : `Order #${selectedIdx + 1}`
                                }
                            </button>
                            {currentItem && isTShirtItem(currentItem) &&
                             currentItem.designImages && Object.keys(currentItem.designImages).length > 0 && (
                                <button
                                    className="preview-tab preview-tab--expand"
                                    onClick={() => setPreviewModalOpen(true)}
                                    title="Expand full preview"
                                >
                                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                                        <circle cx="12" cy="12" r="3"/>
                                    </svg>
                                    Full Preview
                                </button>
                            )}
                        </div>

                        <div className="preview-display">
                            {currentItem && isTShirtItem(currentItem) && (
                                <button className="preview-nav prev" aria-label="Previous"
                                    onClick={() => {
                                        const sideIdx = currentSides.indexOf(activeSide);
                                        if (sideIdx > 0) {
                                            setActiveSideTabs(prev => ({ ...prev, [selectedIdx]: currentSides[sideIdx - 1] }));
                                        } else {
                                            setSelectedIdx(i => Math.max(0, i - 1));
                                        }
                                    }}
                                    disabled={selectedIdx === 0 && currentSides.indexOf(activeSide) === 0}>
                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M19 12H5M5 12L12 19M5 12L12 5"/>
                                    </svg>
                                </button>
                            )}

                            {currentItem && !isTShirtItem(currentItem) && cartItems.length > 1 && (
                                <button className="preview-nav prev" aria-label="Previous"
                                    onClick={() => setSelectedIdx(i => Math.max(0, i - 1))}
                                    disabled={selectedIdx === 0}>
                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M19 12H5M5 12L12 19M5 12L12 5"/>
                                    </svg>
                                </button>
                            )}

                            <div className="preview-image-container">
                                {currentItem && isTShirtItem(currentItem) && (
                                    <SidePreview key={`${selectedIdx}-${activeSide}`} item={currentItem} side={activeSide} />
                                )}
                                {currentItem && !isTShirtItem(currentItem) && (
                                    <ProductImagePreview item={currentItem} />
                                )}
                            </div>

                            {currentItem && isTShirtItem(currentItem) && (
                                <button className="preview-nav next" aria-label="Next"
                                    onClick={() => {
                                        const sideIdx = currentSides.indexOf(activeSide);
                                        if (sideIdx < currentSides.length - 1) {
                                            setActiveSideTabs(prev => ({ ...prev, [selectedIdx]: currentSides[sideIdx + 1] }));
                                        } else {
                                            setSelectedIdx(i => Math.min(cartItems.length - 1, i + 1));
                                        }
                                    }}
                                    disabled={selectedIdx === cartItems.length - 1 && currentSides.indexOf(activeSide) === currentSides.length - 1}>
                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M5 12H19M19 12L12 5M19 12L12 19"/>
                                    </svg>
                                </button>
                            )}

                            {currentItem && !isTShirtItem(currentItem) && cartItems.length > 1 && (
                                <button className="preview-nav next" aria-label="Next"
                                    onClick={() => setSelectedIdx(i => Math.min(cartItems.length - 1, i + 1))}
                                    disabled={selectedIdx === cartItems.length - 1}>
                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M5 12H19M19 12L12 5M19 12L12 19"/>
                                    </svg>
                                </button>
                            )}
                        </div>

                        <div className="order-items-scroll">
                            {cartItems.map((item, index) => (
                                <div key={item.id} className={`order-item-card ${selectedIdx===index?'active':''}`}
                                    onClick={() => setSelectedIdx(index)}>
                                    <img src={item.imageUrl||item.image||''} alt={item.name} className="order-item-card-img"
                                        onError={e => { e.target.style.display='none'; }} />
                                    <div className="order-item-card-label">Order #{index+1}</div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Right: Review panel */}
                    <div className="review-section">
                        <h1 className="review-title">Order Review</h1>

                        <div className="review-info-box">
                            <div className="info-icon">
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                                    <path d="M12 6.01953V6M12 10V18M22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12Z" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                            </div>
                            <p className="info-text">
                                Please review all order details and your design carefully to ensure accuracy.
                                Double-check that all information is correct, spelling is accurate, and text
                                remains clear and readable (at least 8 pt). Once submitted, this design will
                                proceed to printing as provided.
                            </p>
                        </div>

                        {currentItem && <OrderDetailsPanel item={currentItem} index={selectedIdx} />}

                        {!hasAddress && (
                            <div className="no-address-banner">
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
                                </svg>
                                <div className="no-address-text">
                                    <strong>No delivery address on file.</strong>
                                    <span> Add a saved address before submitting. </span>
                                    <Link to="/account/saved-address" className="no-address-link">Add an address →</Link>
                                </div>
                            </div>
                        )}

                        <div className="confirm-design-box">
                            <h3 className="confirm-heading">Confirm Your Design</h3>
                            <p className="confirm-text">
                                Take a moment to double-check your file and details. When you approve your
                                order, you're confirming that everything looks correct. After submission,
                                we'll begin printing and changes can no longer be made.
                                {currentItem && !isTShirtItem(currentItem) && (
                                    <><br /><br />
                                        <strong>Note:</strong> The design for this{' '}
                                        {isPoloItem(currentItem) ? 'Polo Shirt' : 'Jersey'} is
                                        pre-made and provided by JG Printing.
                                    </>
                                )}
                            </p>
                            <label className="confirm-checkbox-label">
                                <input
                                    type="checkbox"
                                    checked={confirmChecked}
                                    onChange={e => setConfirmChecked(e.target.checked)}
                                    className="confirm-checkbox"
                                    disabled={submitting}
                                />
                                <span className="checkbox-text">
                                    I have reviewed my design and order details, and I approve this file for printing.
                                </span>
                            </label>
                        </div>

                        <div className="review-total">
                            <span className="total-label">Total:</span>
                            <span className="total-amount">₱ {grandTotal.toLocaleString()}</span>
                        </div>

                        {submitError && (
                            <div className="or-error-banner" onClick={() => setSubmitError(null)}>
                                {submitError}
                            </div>
                        )}

                        <button
                            className={`submit-approval-btn ${!canSubmit ? 'disabled' : ''}`}
                            onClick={handleSubmit}
                            disabled={!canSubmit}
                            title={!hasAddress ? 'Please add a saved address before submitting' : ''}
                        >
                            {submitting ? 'Submitting...' : 'Submit an Order Approval'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default OrderReview;
